import { useEffect, useRef, useState } from 'react';

import { getView, type DropPoint } from '../../engine/prism.js';
import { api } from '../../lib/api.js';
import { dropLabel, materialAssetOf, planAssetDrop, type DropTarget } from '../../lib/asset-drop.js';
import { draggedAssetPath } from '../../lib/asset-types.js';
import { entityName } from '../../lib/scene-doc.js';
import { logEditor } from '../../state/console.js';
import { useScene } from '../../state/scene.js';
import { useWorkspace } from '../../state/workspace.js';
import { Icon } from '../ui/icons.js';
import './asset-drop.css';

let resyncTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * The browser engine reads assets synchronously from files it preloaded with
 * the scene, so after an edit that references a new file (a dropped model, a
 * material picked in an asset field) the scene view re-reads the document,
 * which preloads the new references.
 */
export function resyncSceneAssets(): void {
  if (resyncTimer) clearTimeout(resyncTimer);
  resyncTimer = setTimeout(() => {
    resyncTimer = null;
    const path = useScene.getState().path;
    if (path) void getView('scene')?.fileChanged(path);
  }, 120);
}

/**
 * Applies a drop of a project file (from the Assets panel) on the scene:
 * the commands from `planAssetDrop`, with the target's material asset read
 * first when a texture needs it. Returns true when something happened.
 */
export async function applyAssetDrop(path: string, target: DropTarget): Promise<boolean> {
  const scene = useScene.getState();
  const doc = scene.doc;
  if (!doc) return false;
  const materials: Record<string, Record<string, unknown>> = {};
  const targetId = target.kind === 'entity' ? target.id : target.kind === 'scene' ? target.hit : null;
  const materialPath = /\.(png|jpe?g|webp|pixel\.json)$/i.test(path) ? materialAssetOf(doc, targetId) : null;
  const projectId = useWorkspace.getState().projectId;
  if (materialPath && projectId) {
    try {
      const res = await api.get<{ file: { content: string } }>(`/projects/${projectId}/file?path=${encodeURIComponent(materialPath)}`);
      materials[materialPath] = JSON.parse(res.file.content) as Record<string, unknown>;
    } catch {
      // Unreadable material: the texture goes on a plain inline material.
    }
  }
  const plan = planAssetDrop(useScene.getState().doc ?? doc, path, target, { materials });
  if (plan.open) {
    await useScene.getState().open(plan.open);
    return true;
  }
  if (plan.message) logEditor('info', plan.message);
  for (const command of plan.commands) {
    const result = useScene.getState().apply(command);
    if (result && !result.ok) return false;
  }
  if (plan.commands.length > 0) resyncSceneAssets();
  return plan.commands.length > 0;
}

/** Drop target for the Hierarchy: `parent` = the row's entity, null = empty space. */
export function hierarchyAssetDrop(dataTransfer: DataTransfer, parent: string | null): boolean {
  const path = draggedAssetPath(dataTransfer);
  if (!path) return false;
  void applyAssetDrop(path, parent ? { kind: 'entity', id: parent } : { kind: 'root' });
  return true;
}

/**
 * Makes the scene view (its `.viewport` parent) accept assets dragged from
 * the Assets panel: models and prefabs land on the surface under the cursor
 * (engine drop raycast), materials / textures / scripts / clips go onto the
 * object under it. Shows what the drop will do while hovering.
 */
export function ViewportAssetDrop() {
  const anchor = useRef<HTMLSpanElement | null>(null);
  const [hint, setHint] = useState<{ x: number; y: number; label: string } | null>(null);

  useEffect(() => {
    const host = anchor.current?.closest<HTMLElement>('.viewport');
    if (!host) return;

    // The engine answers drop points asynchronously (it may run in the engine
    // worker), but dragover must decide at once: hover uses the last answer
    // while the next one is on its way; the drop itself waits for its own.
    let lastHit: DropPoint | null = null;
    let asking = false;
    const ask = (x: number, y: number) => {
      const view = getView('scene');
      if (!view || asking) return;
      asking = true;
      void view
        .dropPoint(x, y)
        .then((hit) => {
          lastHit = hit;
        })
        .finally(() => {
          asking = false;
        });
    };
    const probe = (
      e: DragEvent,
      hit: DropPoint | null,
    ): { path: string; target: DropTarget; label: string | null; x: number; y: number } | null => {
      const path = draggedAssetPath(e.dataTransfer);
      if (!path || !useScene.getState().doc) return null;
      const r = host.getBoundingClientRect();
      const x = e.clientX - r.left;
      const y = e.clientY - r.top;
      const target: DropTarget = { kind: 'scene', point: hit?.point ?? null, hit: hit?.id ?? null };
      const entity = hit?.id ? useScene.getState().doc?.entities.find((en) => en.id === hit.id) : undefined;
      let label = dropLabel(path, Boolean(entity));
      if (label && entity && !/^(Place|Open)/.test(label)) label = `${label} → ${entityName(entity)}`;
      return { path, target, label, x, y };
    };

    const onOver = (e: DragEvent) => {
      const p = probe(e, lastHit);
      if (!p) return;
      ask(p.x, p.y);
      if (!p.label) {
        if (e.dataTransfer) e.dataTransfer.dropEffect = 'none';
        setHint(null);
        return;
      }
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
      setHint({ x: p.x, y: p.y, label: p.label });
    };
    const onLeave = (e: DragEvent) => {
      if (!host.contains(e.relatedTarget as Node | null)) setHint(null);
    };
    const onDrop = (e: DragEvent) => {
      const p = probe(e, lastHit);
      setHint(null);
      if (!p?.label) return;
      e.preventDefault();
      const view = getView('scene');
      void (view ? view.dropPoint(p.x, p.y) : Promise.resolve(null)).then((hit) => {
        lastHit = null;
        // The exact spot under the cursor (the drag data is not readable any more).
        void applyAssetDrop(p.path, hit ? { kind: 'scene', point: hit.point, hit: hit.id } : p.target);
      });
    };
    host.addEventListener('dragover', onOver);
    host.addEventListener('dragleave', onLeave);
    host.addEventListener('drop', onDrop);
    return () => {
      host.removeEventListener('dragover', onOver);
      host.removeEventListener('dragleave', onLeave);
      host.removeEventListener('drop', onDrop);
    };
  }, []);

  return (
    <span ref={anchor} className="viewport-drop-anchor">
      {hint && (
        <span className="viewport-drop-hint" style={{ left: hint.x + 14, top: hint.y + 14 }}>
          <Icon name="plus" size={12} /> {hint.label}
        </span>
      )}
    </span>
  );
}
